/**
 * Usernames nobody may claim.
 *
 * A username is the whole of a seller's public URL (`/s/{username}`), so the
 * damage a bad one does is not cosmetic: a seller called "support" can send a
 * convincing message, and one called "checkout" or "admin" reads as part of the
 * site. The list below is deliberately broad — the cost of reserving a word
 * someone wanted is one rejected form; the cost of releasing the wrong one is a
 * phishing surface we cannot claw back once it is indexed.
 *
 * Reserving these does NOT rename anyone who already holds one. Existing rows
 * are grandfathered; the list only gates new claims.
 */

/**
 * Every top-level route in `/pages` and `/pages/api`, kept in sync by hand.
 *
 * Usernames live under `/s/`, so these can never actually collide with a route.
 * They are reserved anyway because a seller page reachable as "the checkout
 * page" or "the account page" is worth impersonating regardless of the prefix,
 * and because the `/s/` prefix is not guaranteed forever.
 */
const ROUTE_NAMES = [
  'about', 'about-antengo', 'about-us', 'account', 'addresses', 'admin', 'ai',
  'ai-chat', 'analytics', 'api', 'auth', 'browse', 'cart', 'category',
  'categories', 'chat', 'chats', 'checkout', 'cron', 'dash', 'dashboard',
  'debug', 'embed', 'errors', 'faq', 'foryou', 'gateway', 'geolocation',
  'guides', 'health', 'home', 'index', 'investors', 'labs', 'learn-more',
  'listing', 'listings', 'login', 'logout', 'notifications', 'orders',
  'partner', 'partners', 'partner-with-us', 'privacy', 'privacy-policy',
  'product', 'products', 'prohibited-items', 'profile', 'purchases', 'recommendations', 'sales',
  'search', 'sell', 'seller', 'sellers', 'settings', 'shipping', 'shop',
  'signin', 'signout', 'signup', 'stripe', 'tags', 'terms', 'transactions',
  'transfer', 'user', 'users', 'wallet', 'webhook', 'webhooks', 'wishlist',
];

/** Brand and product names. Only Sellwild gets to be Sellwild. */
const BRAND_NAMES = [
  'antengo', 'sellwild', 'sell-wild', 'sellwildtv', 'swtv', 'sellwild-tv',
  'sellwildshorts', 'shorts', 'buyer-guarantee', 'sellwild-buyer-guarantee',
  'official', 'officialsellwild', 'team', 'teamsellwild', 'staff', 'sellwildhq',
];

/**
 * Words that let an account speak as the platform. These are the ones that
 * actually get abused — a message from "billing" or "verification" about a
 * failed payout is a working scam with no other moving parts.
 */
const AUTHORITY_NAMES = [
  'abuse', 'accounts', 'administrator', 'alert', 'alerts', 'billing', 'bot',
  'compliance', 'contact', 'customercare', 'customerservice', 'help',
  'helpdesk', 'info', 'legal', 'mail', 'moderator', 'notification', 'noreply',
  'no-reply', 'payment', 'payments', 'payout', 'payouts', 'postmaster',
  'refund', 'refunds', 'root', 'security', 'service', 'support', 'sysadmin',
  'system', 'trust', 'trustandsafety', 'verification', 'verify', 'webmaster',
  // Operator and moderator handles. "sysop" reads as staff to anyone who has
  // used a forum, and none of these have a legitimate seller use.
  'sysop', 'operator', 'operators', 'op', 'mod', 'mods', 'moderation',
  'moderators', 'employee', 'employees', 'agent', 'agents', 'rep', 'reps',
  'representative', 'escalation', 'escalations', 'supervisor', 'manager',
  // Dispute and claims language. A buyer mid-dispute is exactly the person who
  // will believe a message from "resolution".
  'dispute', 'disputes', 'claim', 'claims', 'arbitration', 'resolution',
  'resolutioncenter', 'mediation', 'appeal', 'appeals', 'ticket', 'tickets',
  'case', 'cases',
  // Safety and fraud desks.
  'safety', 'fraud', 'antifraud', 'risk', 'riskteam', 'audit', 'auditor',
  'investigation', 'investigations', 'enforcement', 'report', 'reports',
  // Credential and auth bait. Anything a phishing message would sign off as.
  'password', 'passwords', 'reset', 'resetpassword', 'credentials', 'secure',
  'security-alert', 'ssl', '2fa', 'mfa', 'otp', 'authcode', 'token', 'apikey',
  'api-key', 'recovery', 'unlock', 'suspended', 'restricted',
];

/**
 * Trust signals. These are claims about an account's standing, and a username
 * is the one place a seller can assert one without it having been earned — the
 * badge next to the name is ours to grant, the name itself is not.
 */
const TRUST_CLAIM_NAMES = [
  'verified', 'verifiedseller', 'verifiedaccount', 'trusted', 'trustedseller',
  'authentic', 'authenticated', 'certified', 'approved', 'endorsed',
  'guarantee', 'guaranteed', 'warranty', 'protection', 'buyerprotection',
  'sellerprotection', 'escrow', 'insured', 'topseller', 'toprated',
  'powerseller', 'preferred', 'premium', 'pro', 'vip', 'elite', 'partner',
];

/**
 * Commerce bait. Less about impersonating staff and more about a name that
 * reads as a site feature rather than a person — a "deals" store page looks
 * curated by Sellwild, and these are also the first names a squatter grabs.
 */
const COMMERCE_BAIT_NAMES = [
  'sale', 'sales', 'deal', 'deals', 'discount', 'discounts', 'coupon',
  'coupons', 'promo', 'promos', 'promotion', 'promotions', 'offer', 'offers',
  'free', 'freebie', 'freebies', 'giveaway', 'giveaways', 'winner', 'winners',
  'prize', 'prizes', 'clearance', 'liquidation', 'wholesale', 'bulk', 'cheap',
  'bestdeal', 'bestdeals', 'featured', 'trending', 'popular', 'recommended',
  'sponsored', 'ad', 'ads', 'advertising',
];

/** Payment and shipping partners users would reasonably read as first-party. */
const PARTNER_NAMES = [
  'affirm', 'afterpay', 'amazon', 'apple', 'applepay', 'dhl', 'ebay',
  'facebook', 'fedex', 'google', 'googlepay', 'klarna', 'mastercard', 'paypal',
  'shippo', 'shopify', 'stripe', 'ups', 'usps', 'venmo', 'visa', 'vendoo',
];

/** Reserved for future first-party use, and the generic mint prefixes. */
const SYSTEM_NAMES = [
  'anonymous', 'deleted', 'guest', 'me', 'new', 'null', 'undefined', 'unknown',
  'you', 'test', 'testing', 'demo', 'example', 'sample', 'default', 'temp',
  'sellwilder', 'seller-', 's',
];

/**
 * The full reserved set, lowercased. Exported for tests and for the admin
 * tooling that needs to explain a rejection.
 */
export const RESERVED_USERNAMES: ReadonlySet<string> = new Set(
  [
    ...ROUTE_NAMES,
    ...BRAND_NAMES,
    ...AUTHORITY_NAMES,
    ...TRUST_CLAIM_NAMES,
    ...COMMERCE_BAIT_NAMES,
    ...PARTNER_NAMES,
    ...SYSTEM_NAMES,
  ].map((word) => word.toLowerCase())
);

/**
 * Prefixes the app itself mints usernames from. `seller-12345` and friends are
 * generated by the phone-signup path, so a user must not be able to hand-claim
 * one and pose as an account the system created.
 */
const RESERVED_PREFIXES = ['seller-', 'sellwild-', 'antengo-', 'admin-'];

/**
 * Whether a candidate is off limits.
 *
 * Compares case-insensitively and ignores separators, so `Sell-Wild`,
 * `sell_wild` and `sellwild` are all caught by the one entry. Without that
 * fold the list would need every punctuation permutation of every word, and
 * would still miss one.
 */
export function isReservedUsername(candidate: string): boolean {
  const lower = candidate.trim().toLowerCase();
  if (!lower) return true;

  if (RESERVED_USERNAMES.has(lower)) return true;
  if (RESERVED_PREFIXES.some((prefix) => lower.startsWith(prefix))) return true;

  // Fold separators away and re-check, so `s-u-p-p-o-r-t` cannot slip past the
  // exact-match pass above.
  const squashed = lower.replace(/[-_.]/g, '');
  if (squashed !== lower && RESERVED_USERNAMES.has(squashed)) return true;

  return false;
}
